<script setup lang="ts">
import type { StorageNode } from '@d/sdk'
import type { FileOperations } from '@d/util'
import type { ToolbarAction } from '../../helper/toolbar'
import type { StorageNodeWithIndex } from './helper/fileLoader'
import { ProjectGrantSymbol, UserSymbol } from '@d/auth/helper/auth'
import { ProjectGrant } from '@d/auth/helper/permission/enum'
import { Scopes } from '@d/auth/helper/scopes/enum'
import { CacheGroup, StorageGetCache, StorageListCache } from '@d/cache'
import { alertQueue } from '@d/form/components/helper/alert'
import { StorageNodeTypeEnum, websocket } from '@d/sdk'
import { Mime, MimeCategory, fileTypes } from '@d/util'
import { mdiAlertCircle, mdiClose, mdiDeleteForever, mdiDownload, mdiEye, mdiFileMove, mdiFileMultiple, mdiFileRemove, mdiFileRestore, mdiFolderMove, mdiFolderMultiple, mdiFolderRemove, mdiFolderText, mdiFullscreen, mdiFullscreenExit, mdiHandExtended, mdiHistory, mdiLink, mdiLock, mdiLockOpen, mdiPencil, mdiPlus, mdiPrinter, mdiRename, mdiSwapHorizontal } from '@mdi/js'
import { useTreeView } from '../../composables/useTreeView'
import { icon } from '../../helper/icon'
import { setTitle } from '../../helper/pages.js'
import { useStateScope } from '../../helper/shortcut'
import { FileAction } from '../../helper/toolbar'
import UDialog from '../dialogs/UDialog.vue'
import ULoading from '../util/ULoading.vue'
import { clickDownload, download as downloadHelper } from './helper/download'
import EditingHint from './helper/EditingHint.vue'

interface Props {
  file?: StorageNodeWithIndex
  path: string[]
  client: string
}

const props = withDefaults(defineProps<Props>(), {
  file: undefined,
})

const file = useVModel(props, 'file')
const path = ref<string[]>(props.path)

const FileActionDialog = defineAsyncComponent(async () => await import('../dialogs/toolbar/FileActionDialog.vue'))

const user = inject(UserSymbol)

const shortcutScope = useStateScope()

onUnmounted(() => {
  if (shortcutScope)
    shortcutScope.dialog.value = false
})

const isStorage = computed(() => props.path.length === 1)

const myOrg = computed(() => user?.value?.profile[Scopes.ResourceOwnerName])
const { t } = useI18n(
  {
    useScope: 'local',
  },
)

// const layoutController = useLayoutController('default')
// layoutController.setValue('rightDrawer', true)
const treeView = useTreeView()

const activeDialog = ref<FileAction>(FileAction.CREATE)
const dialog = ref(false)

watch(dialog, (val) => {
  if (shortcutScope) {
    shortcutScope.dialog.value = val
  }
}, { immediate: true })

const showHint = useLocalStorage('showHint', true)
const disableAnimations = useLocalStorage('disableAnims', false)

const showBanner = ref<boolean>(true)

const cacheGroup = new CacheGroup()

const node = StorageGetCache.useReactive(() => props.path.join('/'), cacheGroup)
// const node = ref(PathGuide.from(route.params.path).useGet(cacheGroup))

// watch(() => route.params.path?.join('/'), () => {
//   node.value = PathGuide.from(route.params.path).useGet(cacheGroup)
// })
onMounted(() => {
  const p = props.path.join('/')
  treeView.setSelected(p)
})

const fullscreen = ref<boolean>(false)

const projectGrants = inject(ProjectGrantSymbol)

function hasGrant(permission: ProjectGrant): boolean {
  return projectGrants?.value?.includes(permission) ?? false
}

const actions = ref<ToolbarAction[]>([])

const parentManual = asyncComputed(async () => {
  return await node.value.$.findParentManual()
})

const isLocked = ref<boolean>(false)
const lockLoading = ref<boolean>(false)

/**
 * Reloads the lock state for the current file to check if file actions have to be re-enabled.
 */
async function refreshLock(locked: boolean) {
  if (lockLoading.value)
    return
  try {
    lockLoading.value = true
    // If the state changed, reload file actions
    if (isLocked.value !== locked) {
      isLocked.value = locked
      const isInsideBin = await node.value.$.isInsideTrashBin()
      const v = await node.value.tryValueAsync
      if (!v)
        return
      const fileType = (node.value.$.isFile() && v.mime) ? (fileTypes[v.mime] ?? fileTypes.raw) : fileTypes.raw
      const updatedRes = await searchIndex(v)
      if (updatedRes) {
        actions.value = getToolbarActions(updatedRes.node, isInsideBin, fileType?.actions, isLocked.value)
      }
      else {
        actions.value = getToolbarActions(v, isInsideBin, fileType?.actions, isLocked.value)
      }

      if (!locked) {
        alertQueue.value.push({ text: t('actionLockDone'), color: 'success', timeout: 5000, icon: mdiLockOpen })
      }
      else {
        alertQueue.value.push({ text: t('actionLockStart'), color: 'warning', timeout: 5000, icon: mdiLock })
      }
    }
  }
  catch {
    isLocked.value = false
  }
  finally {
    lockLoading.value = false
  }
}

const isValid = ref<boolean>(true)
const fatalError = ref<boolean>(false)

async function searchIndex(res: StorageNode): Promise<{ node: StorageNodeWithIndex, filename: string } | undefined> {
  const routePath = props.path.join('/')
  if (res.type === StorageNodeTypeEnum.File)
    return undefined
  const node = StorageListCache.useReactive(() => routePath, cacheGroup)
  const dir = await node.value.tryValueAsync
  if (!dir)
    return undefined
  const indexFile = dir.find(i => i.name === 'index.md' && i.type === StorageNodeTypeEnum.File && i.mime === Mime['text/markdown'])
  if (!indexFile)
    return undefined
  const updatedRes = {
    ...res,
    index: true,
    size: indexFile.size,
  }
  return { filename: indexFile.name, node: updatedRes }
}

async function getFile(): Promise<StorageNodeWithIndex> {
  path.value = []
  let res = await node.value.tryValueAsync
  let indexFilename: string | null = null
  if (!res) {
    isValid.value = false
    throw new Error('File not found')
  }

  if (myOrg.value) {
    setTitle(`${myOrg.value} | ${res.name.length === 0 ? props.path[0] : res.name}`)
  }
  else {
    setTitle(res.name.length === 0 ? props.path[0] : res.name)
  }

  // Search for index
  const updatedRes = await searchIndex(res)
  if (updatedRes) {
    res = updatedRes.node
    indexFilename = updatedRes.filename
  }
  // Set toolbar actions
  const isInsideBin = await node.value.$.isInsideTrashBin()
  const fileType = (res.type === StorageNodeTypeEnum.File && res.mime) ? (fileTypes[res.mime] ?? fileTypes.raw) : fileTypes.raw
  actions.value = getToolbarActions(res, isInsideBin, fileType?.actions, isLocked.value)

  path.value = indexFilename ? [...props.path, indexFilename] : [...props.path]
  isValid.value = true
  return res as StorageNodeWithIndex
}

// Load file async to have it instantly ready
try {
  file.value = await getFile()
}
catch {}

watch(isValid, async (valid) => {
  if (!valid && !fatalError.value) {
  // Wait for a small time
    await new Promise(resolve => setTimeout(resolve, 1000))
    try {
      file.value = await getFile()
      if (file.value) {
        fatalError.value = false
        isValid.value = true
      }
      else {
        fatalError.value = true
        isValid.value = false
      }
    }
    catch {
      fatalError.value = true
      isValid.value = false
    }
  }
}, { immediate: true })

const monacoTypes = markRaw<MimeCategory[]>([
  MimeCategory.Code,
  MimeCategory.Text,
])

// Preview components
const UFileToolbar = defineAsyncComponent(() => import('./UFileToolbar.vue'))
const UTagToolbar = defineAsyncComponent(() => import('./UTagToolbar.vue'))

const DownloadCard = defineAsyncComponent(() => import('./DownloadCard.vue'))
const UTextPreview = defineAsyncComponent(() => import('./UTextPreview.vue'))
const ReadMarkdown = defineAsyncComponent(() => import('../markdown/ReadMarkdown.vue'))
const UImagePreview = defineAsyncComponent(() => import('./UImagePreview.vue'))
const UPDFPreview = defineAsyncComponent(() => import('./UPDFPreview.vue'))
const NoIndex = defineAsyncComponent(() => import('../directory/Read.vue'))

type ComponentType = typeof DownloadCard | typeof UTextPreview | typeof ReadMarkdown | typeof UImagePreview | typeof UPDFPreview | typeof NoIndex

const componentMap: [() => boolean, ComponentType][] = [
  [() => node.value.$.isFile() && node.value.$.isMimeType(MimeCategory.Markdown), ReadMarkdown],
  [() => node.value.$.isFile() && node.value.$.isMimeType(MimeCategory.Pdf), UPDFPreview],
  [() => node.value.$.isFile() && monacoTypes.some(node.value.$.isMimeType), UTextPreview],
  [() => node.value.$.isFile() && node.value.$.isMimeType(MimeCategory.Image), UImagePreview],
  [() => node.value.$.isFile() && node.value.$.isMimeType(MimeCategory.Code), UTextPreview],
  [() => node.value.$.isFile(), DownloadCard],
  [() => (file.value && file.value.index) ?? false, ReadMarkdown],
  [() => true, NoIndex], // Default fallback
]

const viewComponent = computed(() => {
  const entry = componentMap.find(([cond]) => cond())
  return entry?.[1] ?? NoIndex
})

const tags = ref<Set<string> | undefined>(undefined)

/* const fullWidth = computed(() => {
  if (file.value? && file.value?.type === StorageNodeTypeEnum.File && fileType.value) {
    return monacoTypes.includes(fileType.value.category)
  }
  return false
}) */

async function openDialog(action: FileAction): Promise<void> {
  const lockedActions = [FileAction.DELETEFINALLY, FileAction.DELETE, FileAction.MOVE, FileAction.RENAME, FileAction.CONVERT]
  if (lockedActions.includes(action)) {
    if (isLocked.value) {
      alertQueue.value.push({ text: t('actionLock'), timeout: 5000, color: 'warning' })
      return
    }
  }
  activeDialog.value = action
  dialog.value = true
}

async function download(): Promise<void> {
  const blob = await downloadHelper(treeView.selectedElementPath.value)
  clickDownload(blob, treeView.selectedElement.value.name)
}

async function edit(index?: boolean): Promise<void> {
  const editPath = treeView.selectedElementPath.value.split('/')
  if (index) {
    editPath.push('index.md')
  }
  await navigateTo({ name: 'client-write-path', params: { client: props.client, path: editPath } })
}

async function showIndex(): Promise<void> {
  const showPath = treeView.selectedElementPath.value.split('/')
  showPath.push('index.md')
  await navigateTo({ name: 'client-view-path', params: { client: props.client, path: showPath } })
}

function getToolbarActions(file: StorageNode & { index?: boolean }, isInsideBin: boolean = false, actions_?: FileOperations, locked: boolean = false): ToolbarAction[] {
  const directory = file.type !== StorageNodeTypeEnum.File
  return [
    {
      icon: mdiPlus,
      action: () => openDialog(FileAction.CREATE),
      tooltip: t('create'),
      condition: directory && file.features.write && hasGrant(ProjectGrant.write) && !isInsideBin, // TODO: Add check if user may create
      position: 'left',
      shortcut: 'alt+n',
    },
    {
      icon: mdiDownload,
      action: download,
      tooltip: t('download'),
      condition: file.features.read && (actions_?.download === true) && hasGrant(ProjectGrant.read),
      position: 'left',
      shortcut: 'alt+d',
    },
    {
      icon: mdiPencil,
      action: () => edit(),
      tooltip: locked ? t('requestEdit') : t('edit'),
      condition: file.features.write && (actions_?.edit === true || node.value.$.isManual()) && hasGrant(ProjectGrant.write) && !isInsideBin,
      position: 'left',
      shortcut: 'alt+e',
    },
    {
      icon: directory ? mdiFolderMultiple : mdiFileMultiple,
      action: () => openDialog(FileAction.COPY),
      tooltip: t('copy'),
      condition: file.features.write && (actions_?.copy === true) && hasGrant(ProjectGrant.write) && !isStorage.value && !isInsideBin,
      position: 'left',
      shortcut: 'alt+c',
    },
    {
      icon: directory ? mdiFolderMove : mdiFileMove,
      action: () => openDialog(FileAction.MOVE),
      tooltip: t('move'),
      disabled: locked,
      condition: file.features.write && (actions_?.move === true) && hasGrant(ProjectGrant.write) && !isStorage.value && !isInsideBin,
      position: 'left',
      shortcut: 'alt+m',
    },
    {
      icon: mdiRename,
      action: () => openDialog(FileAction.RENAME),
      tooltip: t('rename'),
      disabled: locked,
      condition: file.features.write && (actions_?.move === true) && hasGrant(ProjectGrant.write) && !isStorage.value && !isInsideBin,
      position: 'left',
      shortcut: 'alt+r',

    },
    {
      icon: directory ? mdiFolderRemove : mdiFileRemove,
      action: () => openDialog(FileAction.DELETE),
      tooltip: t('delete'),
      disabled: locked,
      condition: file.features.write && (actions_?.delete === true) && hasGrant(ProjectGrant.write) && !isStorage.value && !isInsideBin,
      color: 'error',
      position: 'left',
      shortcut: 'alt+del',
    },
    {
      icon: mdiDeleteForever,
      action: () => openDialog(FileAction.DELETEFINALLY),
      tooltip: t(`deleteFinally.${file.type}`),
      disabled: locked,
      condition: (isInsideBin || file.type === StorageNodeTypeEnum.Bin) && file.features.trashbin,
      color: 'error',
      position: 'left',
    },
    {
      icon: mdiFileRestore,
      action: () => openDialog(FileAction.RESTOREFILE),
      tooltip: t('restoreFile'),
      disabled: locked,
      condition: isInsideBin && file.features.trashbin,
      color: 'success',
      position: 'left',
    },

    // -------------------------
    {
      icon: mdiHandExtended,
      action: () => openDialog(FileAction.SHARELINK),
      condition: !node.value.$.path().isStorage && !isInsideBin && file.type !== StorageNodeTypeEnum.Bin,
      tooltip: t('sharelink'),
      position: 'right',
      shortcut: 'alt+l',
    },
    {
      icon: mdiLink,
      action: () => openDialog(FileAction.REFERENCES),
      condition: !isInsideBin,
      tooltip: t('references'),
      position: 'right',
    },
    {
      icon: mdiHistory,
      action: () => openDialog(FileAction.VERSIONING),
      condition: !isInsideBin && !node.value.$.isManual() && file.features.versioning && (node.value.$.isMimeType(MimeCategory.Markdown) || node.value.$.isMimeType(MimeCategory.Image) || node.value.$.isMimeType(MimeCategory.Text)),
      tooltip: t('changes'),
      position: 'right',
      shortcut: 'alt+h',
    },
    {
      icon: mdiSwapHorizontal,
      action: () => openDialog(FileAction.CONVERT),
      disabled: locked,
      condition: !parentManual.value && file.name.endsWith('.md') && file.features.write && (actions_?.copy === true) && node.value.$.isMimeType(MimeCategory.Markdown) && hasGrant(ProjectGrant.write) && !isInsideBin,
      tooltip: t('convert'),
      position: 'right',
    },
    {
      icon: mdiEye,
      action: () => showIndex(),
      condition: directory && (file.index ?? false) && file.features.write && hasGrant(ProjectGrant.write) && !isInsideBin,
      tooltip: t('showIndex'),
      disabled: locked,
      position: 'right',
      shortcut: file.features.write && (actions_?.edit === true || node.value.$.isManual()) && hasGrant(ProjectGrant.write) && !isInsideBin ? undefined : 'alt+e',
    },
    {
      icon: mdiPrinter,
      action: () => {
        const preview = document.querySelector('.up-md-preview')
        if (!preview)
          return
        const clone = preview.cloneNode(true) as HTMLElement
        clone.classList.add('print-clone')
        // Remove the TOC toggle button from the clone
        clone.querySelector('.v-tooltip')?.remove()
        document.body.appendChild(clone)
        window.print()
        document.body.removeChild(clone)
      },
      condition: !parentManual.value && file.name.endsWith('.md') && node.value.$.isMimeType(MimeCategory.Markdown) && !isInsideBin,
      tooltip: t('print'),
      iconOnly: true,
      position: 'right',
      shortcut: 'alt+p',
    },
    {
      icon: mdiFullscreen,
      action: () => fullscreen.value = !fullscreen.value,
      condition: file.features.read,
      tooltip: t('fullscreen'),
      iconOnly: true,
      position: 'right',
      shortcut: 'alt+f11',
    },
  ]
}

const componentRef = ref<InstanceType<ComponentType> | undefined>(undefined)
const loaded = ref<boolean>(false)
const showToolbar = computed(() => node.value && !node.value.loading && !node.value.failed && loaded.value)
const validDocument = computed(() => node && !node.value.loading && !node.value.failed)
const toUserMenu = async () => await navigateTo({ name: 'client-me', params: { client: props.client } })

async function wsLock(data: { path: string, lock: boolean }) {
  if (data.path === path.value.join('/')) {
    await refreshLock(data.lock)
  }
}

function setLock(path: string[]) {
  websocket.on('documentLock', wsLock)
  websocket.on('subscribeLock', wsLock)
  websocket.emit('subscribeLock', path.join('/'))
}

setLock(path.value)

onUnmounted(() => {
  if (websocket.connected) {
    websocket.emit('unsubscribeLock', path.value.join('/'))
    websocket.off('subscribeLock', wsLock)
    websocket.off('documentLock', wsLock)
  }
})

watch(websocket.connected, async (newVal, oldVal) => {
  if (oldVal === newVal)
    return

  if (newVal) {
    setLock(path.value)
  }
  else {
    websocket.off('subscribeLock', wsLock)
    websocket.off('documentLock', wsLock)
  }
})

watch(() => props.path, async (newVal, oldVal) => {
  if (oldVal.join('/') === newVal.join('/') || !websocket.connected.value)
    return
  websocket.emit('unsubscribeLock', oldVal.join('/'))
  websocket.off('subscribeLock', wsLock)
  websocket.off('documentLock', wsLock)
  setLock(newVal)
})
</script>

<template>
  <FileActionDialog
    v-if="dialog && treeView.selectedElement.value"
    v-model="dialog"
    v-model:locked="isLocked"
    :action="activeDialog"
    :path="treeView.selectedElementPathArr.value"
    :file="treeView.selectedElement.value"
    :disable-animations="disableAnimations"
    :storage-features="file!.features"
  />

  <!-- File loading -->
  <template v-if="!node || node.loading || (!isValid && !fatalError)">
    <ULoading class="view-component" />
  </template>

  <!-- Error -->
  <template v-else-if="!isValid && fatalError">
    <div class="empty-page">
      <div class="empty-container">
        <v-empty-state
          :headline="t('error.headline')"
          :title="t('error.title')"
          :text="t('error.text')"
          color="error"
          :icon="mdiAlertCircle"
        />
      </div>
    </div>
  </template>

  <template v-else>
    <!-- Toolbar -->
    <UFileToolbar
      v-if="validDocument"
      v-show="loaded"
      v-model="actions"
      :loading="!showToolbar"
      :disable-shortcuts="shortcutScope?.dialog.value"
    />

    <!-- Hint for locked file -->
    <EditingHint v-show="showToolbar && !fullscreen" v-model:show="isLocked" />

    <!-- Tags -->
    <div v-show="loaded">
      <UTagToolbar
        v-if="(node.$.isMimeType(MimeCategory.Markdown) || file?.index)"
        v-model:tags="tags"
        :loading="!showToolbar"
        :empty-message="t('noTags')"
        mode="read"
      />
    </div>

    <!-- Hint for index file -->
    <v-banner
      v-if="file?.index && showHint && showBanner"
      v-show="showToolbar"
      bg-color="warning"
      density="compact"
      style="display: flex;"
    >
      <template #default>
        <div class="d-flex align-center justify-space-between flex-grow-1">
          <div class="d-flex align-center">
            <v-icon :icon="mdiFolderText" />
            <span class="ml-3">{{ t('indexHint') }}</span>
          </div>
          <div>
            <v-tooltip>
              <template #activator="{ props: activator }">
                <v-btn
                  v-bind="activator"
                  variant="text"
                  :icon="mdiClose"
                  @click.left.prevent="() => showBanner = false"
                  @click.right.prevent="toUserMenu"
                />
              </template>
              <span>{{ t('hideHintPermanent') }}</span>
            </v-tooltip>
          </div>
        </div>
      </template>
    </v-banner>

    <!-- Fullscreen Dialog -->
    <UDialog
      v-if="fullscreen && file"
      v-model="fullscreen"
      v-model:fullscreen="fullscreen"
      :close-icon="mdiFullscreenExit"
      :title-icon="icon(file)"
      :title="file.index ? path.at(-2) : (file.name || path.at(-1))"
      scrollable
      closable
      :disable-animations="disableAnimations"
    >
      <Suspense suspensible @resolve="() => loaded = true">
        <component
          :is="viewComponent"
          ref="componentRef"
          v-model:tags="tags"
          v-model:file="file"
          class="view-component"
          :path="path"
          fullscreen
        />
      </Suspense>
    </UDialog>

    <!-- Main view -->
    <template v-else-if="validDocument">
      <div class="d-flex flex-column overflow-auto" style="height: 100%;">
        <Suspense suspensible @resolve="() => loaded = true">
          <component
            :is="viewComponent"
            ref="componentRef"
            v-model:tags="tags"
            v-model:file="file"
            class="view-component"
            :path="path"
          />
        </Suspense>
      </div>
    </template>
  </template>
</template>

<style scoped>
.view-component {
  padding: 0px;
  height: 100%
}

.max-full-width {
  max-width: 100% !important;
}

:deep(.v-banner__content) {
  flex-grow: 1;
}

.empty-container{
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 12px;
  height: fit-content
}

.empty-page{
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center
}
</style>

<i18n lang="yaml">
  de:
    references: Referenzen
    changes: Änderungen
    copy: Kopieren
    move: Verschieben
    rename: Umbenennen
    delete: Löschen
    edit: Bearbeiten
    requestEdit: Bearbeitung anfragen
    download: Herunterladen
    create: Erstellen
    fullscreen: Vollbild
    showIndex: Index anzeigen
    indexHint: Inhalt der index.md
    hideHintPermanent: Die Anzeige kann im Benutzermenü (Rechtsklick) permanent deaktiviert werden
    refreshLock: Status aktualisieren
    actionLock: Die Aktion kann nicht ausgeführt werden, da die Datei zurzeit bearbeitet wird.
    actionLockDone: Die Bearbeitung wurde freigegeben
    actionLockStart: Die Datei wird derzeit von einem anderen Benutzer bearbeitet
    convert: Umwandeln
    noTags: Keine Tags vorhanden
    error:
      headline: Laden des Elements fehlgeschlagen
      title: Element nicht gefunden
      text: Überprüfe, ob das Element existiert und der zugehörige Storage erreichbar ist
    deleteFinally:
      bin: Papierkorb Leeren
      directory: Endgültig Löschen
      manual: Endgültig Löschen
      file: Endgültig Löschen
    restoreFile: Wiederherstellen
    print: Exportieren
    sharelink: Link Teilen

  en:
    references: References
    changes: Changes
    delete: Delete
    edit: Edit
    requestEdit: Request edit
    download: Download
    create: Create
    copy: Copy
    move: Move
    rename: Rename
    fullscreen: Fullscreen
    showIndex: Show Index
    indexHint: Content of the index.md
    hideHintPermanent: The display can be permanently deactivated in the user menu (right click)
    refreshLock: Update status
    actionLock: The action cannot be performed because the file is currently being edited.
    actionLockDone: Editing completed, file actions unlocked
    actionLockStart: The file is being edited by another user
    convert: Convert
    error:
      headline: Failed to load the element
      title: Element not found
      text: Check whether the element exists and the associated storage is accessible
    deleteFinally:
      bin: Delete Recycle Bin
      directory: Delete finally
      manual: Delete finally
      file: Delete finally
    restoreFile: Restore
    print: Export
    sharelink: Sharelink
  </i18n>
